import React, { useEffect, useState, ChangeEvent, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
} from 'reactstrap';
import { useUser } from 'context/UserContext';
import { useAlertPlus, useConfirm } from 'hooks';
import Required from 'components/Required';
import ConfirmModal from 'components/ConfirmModal';
import AlertModal from 'components/AlertModal';
import AltaStaff from 'components/StaffMultiple/AltaStaff';
import BajaStaff from 'components/StaffMultiple/BajaStaff';
import UploaderMultiple from 'components/UploaderMultiple';
import HonorariosEntidades from 'components/MatriculateRequest/HonorariosEntidades';
import { useHistory } from 'react-router-dom';
import {
  getAllConsultoriosAsDirector,
  getConsultorioInfoWithStaff,
} from '../../features/consultorios/consultoriosAPI';
import {
  postConsultorioStaffMultiple,
} from '../../features/staff/staffAPI';
import {
  ConsultorioCokibaBD,
} from 'types/Consultorio';
import { validarSolapamientos } from '../../utils/horariosValidacion';
import {
  StaffMultipleRequest,
  AltaStaffMultiple,
  StaffConsultorio,
} from 'types/Staff';
import { useTotp } from 'hooks/useTotp';

export default function CreateSolicitudStaffMultiple() {
  const { getMatricula } = useUser();
  const matriculateId = getMatricula();
  if (!matriculateId) {
    return null;
  }

  const history = useHistory();

  const [formValidation, setFormValidation] = useState<Record<string, boolean>>({});

  const [disableSend, setDisableSend] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const formElement = useRef<HTMLFormElement | null>(null);

  const [consultorios, setConsultorios] = useState<ConsultorioCokibaBD[]>([]);
  const [allStaff, setAllStaff] = useState<StaffConsultorio[]>([]);

  const [data, setData] = useState<StaffMultipleRequest>({
    id: 0,
    state_id: null,
    matriculate_id: +matriculateId,
    consultorio_id: null,
    staff_alta: [],
    staff_baja: [],
    created_at: null,
    resolved_at: null,
    deleted_at: null,
    user_id: null,
    motivo_rechazo: null,
    state: null,
    attachs: [],
  });

  const { requestTotp, TotpModal } = useTotp();

  useEffect(() => {
    setLoading(true);

    const loadData = async () => {
      try {
        getAllConsultoriosAsDirector()
          .then(response => {
            setConsultorios(response);
          })
          .catch(err => {
            alert(true, err.getMessage() || 'No se puedieron obtener los pedidos del matriculado');
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (err) {
        setDisableSend(true);
        alert(
          'Se produjo un error al intentar recuperar información inicial. ' +
            'Por favor, intente nuevamente más tarde.',
        );
        console.error('ConsultoriosCreate', 'Error al cargar datos iniciales');
        console.info(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    const loadConsultorioStaff = async (id: number) => {
      try {
        getConsultorioInfoWithStaff(id)
          .then(response => {
            setAllStaff(response?.allStaff ?? []);
          })
          .catch(err => {
            alert(true, err.getMessage() || 'No se puedieron obtener los pedidos del matriculado');
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (err) {
        setDisableSend(true);
        alert(
          'Se produjo un error al intentar recuperar información del consultorio. ' +
            'Por favor, intente nuevamente más tarde.',
        );
        console.error('StaffMultipleCreate', 'Error al cargar datos iniciales');
        console.info(err);
      } finally {
        setLoading(false);
      }
    };

    if (!!data.consultorio_id) {
      setLoading(true);
      loadConsultorioStaff(data.consultorio_id);
    } else {
      setAllStaff([]);
    }
  }, [data.consultorio_id]);

  const validarHorarios = (staff: AltaStaffMultiple): boolean => {
    if (staff.horarios_atencion.length === 0) {
      alert(`Se debe cargar al menos un día y horarios de atención.`);
      return false;
    }

    const resultado = validarSolapamientos(staff.horarios_atencion, staff.horarios_atencion_act);
    if (!resultado.ok) {
      alert(resultado.message ?? 'Hay horarios superpuestos.');
      return false;
    }

    return true;
  };

  const handleFormValidation = () => {
    if (data.staff_alta.length == 0 && data.staff_baja.length == 0) {
      alert(
        'Debe agregar los matriculados para el Alta o Baja en el Staff.',
      );
      return false;
    }

    let missing = false;
    const newFormValidation: Record<string, boolean> = {};

    const toValidate = Array.from(document.getElementsByClassName('form-control')) as HTMLInputElement[];
    toValidate.forEach(field => {
      if (!field.disabled && field.required && !field.value?.trim()) {
        field.classList.add('is-invalid');
        missing = true;
        newFormValidation[field.name] = false;
      } else {
        field.classList.remove('is-invalid');
        newFormValidation[field.name] = true;
      }

      // Validación numérica
      if (field.inputMode === 'numeric' && !/^\d+$/.test(field.value)) {
        newFormValidation[field.name] = false;
        missing = true;
      }
    });

    for (const staff of data.staff_alta) {
      if (!staff.matriculate_id) {
        alert(
          'Debe realizar la búsqueda (🔍) de cada matriculado que se dé de alta en el Staff.',
        );
        return false;
      }

      if (!validarHorarios(staff)) {
        setFormValidation(newFormValidation);
        return false;
      }
    }

    setFormValidation(newFormValidation);

    if (missing) {
      alert(
        'Todos los campos obligatorios deben completarse correctamente antes de enviar la solicitud.',
      );
      return false;
    }

    return true;
  };

  // Confirmación de envío de Solicitud
  const handleSendForm = () => {
    if (!handleFormValidation()) {
      return;
    }

    setConfirm(
      true,
      '¿Estás seguro que deseas enviar esta solicitud de consultorio?',
      'send',
    );
  };

  // Gestor de respuestas de confirmaciones
  const handleConfirmation = (action: string) => {
    if (action === 'send') {
      handleSend();
    }

    setConfirm(false);
  };

  // Envío de solicitudes
  const handleSend = async () => {
    setFormValidation({});

    setLoading(true);

    // Preparamos los inputs menos los archivos
    const consultorioStaff: Partial<StaffMultipleRequest> = {};

    if (!formElement.current) return;

    new FormData(formElement.current).forEach((value, key) => {
      // @ts-ignore
      consultorioStaff[key as keyof StaffMultipleRequest] = value.toString() as any;
    });

    consultorioStaff['staff_alta'] = data.staff_alta;
    consultorioStaff['staff_baja'] = data.staff_baja;

    for (const staffBaja of consultorioStaff['staff_baja']) {
      const selectedStaff = allStaff.find(
        staff => staff.matricula === staffBaja.matriculate_id,
      );
      if (selectedStaff) {
        staffBaja['es_director'] = selectedStaff.is_codirector;
      }
    }

    // Preparamos los archivos
    const files: Record<string, File> = {};
    const inputs = Array.from(document.querySelectorAll('input[type="file"]'));
    for (const inputElement of inputs) {
      const input = inputElement as HTMLInputElement;
      if (input.hasAttribute('required') && input.value === '') {
        alert(
          'Faltan subir archivos requeridos para poder enviar la solicitud.',
        );
        setLoading(false);
        return;
      }

      if (input.files) {
        for (let i = 0; i < input.files.length; i++) {
          files[`${input.name}_${i}`] = input.files[i];
        }
      }
    }

    const code = await requestTotp('staff_multiple');
    if (!code) return;

    // Enviamos la petición
    await postConsultorioStaffMultiple(consultorioStaff, files, code)
      .then(res => {
        if (!res || res.status != 'ok') {
          // Error interno
          console.error('ConsultoriosStaffCreate', 'Error al enviar la solicitud');
          console.info(res);
          alert(
            res.message ??
            'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
          );
          return;
        }

        alert('La solicitud se envió correctamente', () => {
          history.push('/consultorios');
        });
      })
      .catch(err => {
        console.error(
          'ConsultoriosStaffCreate',
          'Error inesperado al intentar enviar la solicitud',
        );
        console.info(err);
        alert(
          'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;

    const name = target.name;

    let value: string | number | boolean | null;
    value = target.value;

    if (target.type === 'checkbox') {
      value = target.checked;
    }

    if (
      typeof value === 'string' &&
      ['consultorio_id'].includes(name)
    ) {
      value = !!parseInt(value) ? parseInt(value) : '';
    }

    // Guardamos el nuevo valor
    setData({
      ...data,
      [name]: value,
    });
  };

  const agregarAltaStaff = () => {
    setData(prev => ({
      ...prev,
      staff_alta: [
        ...prev.staff_alta,
        {
          id: Date.now(),
          matriculate_id: 0,
          es_director: false,
          realizado: false,
          horarios_atencion: [],
          horarios_atencion_act: [],
          attachs: [],
        },
      ],
    }));
  };

  const eliminarAltaStaff = (id: number) => {
    const nuevosAltaStaff = data.staff_alta.filter(staff => staff.id !== id);
    setData(prev => ({
      ...prev,
      staff_alta: nuevosAltaStaff,
    }));
  };

  const agregarBajaStaff = () => {
    setData(prev => ({
      ...prev,
      staff_baja: [
        ...prev.staff_baja,
        {
          id: Date.now(),
          matriculate_id: 0,
          es_director: false,
          realizado: false,
          attachs: [],
        },
      ],
    }));
  };

  const eliminarBajaStaff = (id: number) => {
    const nuevosBajaStaff = data.staff_baja.filter(staff => staff.id !== id);
    setData(prev => ({
      ...prev,
      staff_baja: nuevosBajaStaff,
    }));
  };

  return (
    <div className="content">
      <Row>
        <Col sm={12}>
          <Card>
            <CardHeader>
              <Row style={{ alignItems: 'center' }}>
                <Col sm={8}>
                  <CardTitle tag="h5">Staff Consultorio</CardTitle>
                </Col>
                <Col sm={4} className="text-right">
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => history.push('/consultorios')}
                  >
                    Volver
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Form innerRef={formElement}>
                <Col sm={12}>
                  <CardTitle>Consultorio</CardTitle>
                </Col>
                <FormGroup>
                  <Row>
                    <Col md="6">
                      <Label for="consultorio_id">
                        Consultorio <Required />
                      </Label>
                      <Input
                        id="consultorio_id"
                        name="consultorio_id"
                        type="select"
                        className="custom-select form-control"
                        value={data.consultorio_id ?? ''}
                        invalid={formValidation.consultorio_id === false}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        required
                      >
                        <option value="">Seleccione una opción...</option>
                        {consultorios?.map(item => {
                          return (
                            <option
                              key={item.id}
                              value={item.id}
                            >
                              {item.nombre}, {item.calle} {item.numero}, {item.localidad?.MTR008_D_LOCALIDAD}
                            </option>
                          );
                        })}
                      </Input>
                    </Col>
                    <Col md="6" className="d-flex align-items-end">
                      <Button className="btn-round" color="primary" onClick={agregarAltaStaff}>
                        + Alta Staff
                      </Button>
                      <Button className="btn-round" color="primary" onClick={agregarBajaStaff}>
                        + Baja Staff
                      </Button>
                    </Col>
                  </Row>
                </FormGroup>
                <hr className="my-4" />
                <Col sm={12}>
                  <CardTitle>Altas</CardTitle>
                </Col>
                {
                  data.staff_alta.map(staff => {
                    return (
                      <AltaStaff
                        key={staff.id}
                        isLoading={isLoading}
                        setLoading={setLoading}
                        staff={staff}
                        data={data}
                        setData={setData}
                        formValidation={formValidation}
                        eliminarAltaStaff={eliminarAltaStaff}
                      />
                    );
                  })
                }
                <hr className="my-4" />
                <Col sm={12}>
                  <CardTitle>Bajas</CardTitle>
                </Col>
                {
                  data.staff_baja.map(staff => {
                    return (
                      <BajaStaff
                        key={staff.id}
                        isLoading={isLoading}
                        staff={staff}
                        data={data}
                        setData={setData}
                        formValidation={formValidation}
                        allStaff={allStaff}
                        eliminarBajaStaff={eliminarBajaStaff}
                      />
                    );
                  })
                }
                <hr className="my-4" />
                <Col sm={12}>
                  <CardTitle>Adjuntos</CardTitle>
                </Col>
                <Col sm={12}>
                  <HonorariosEntidades
                    titulosPagos={[
                      'ARBA_TIMBRADO_EN_BLANCO',
                    ]}
                  />
                  <p>(el precio indicado se aplica de manera individual por cada alta o baja de staff)</p>
                </Col>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="constancia_pagos">
                        Constancia de pago <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="constancia_pagos"
                        required
                      />
                    </Col>
                    <Col md="6">
                      <Label for="solicitud_habilitacion">
                        Nota Tipo 6 solicitando la habilitación (con firma del director técnico)<Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="solicitud_habilitacion"
                        required
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="resolucion_ministerio">
                        Resolución del Ministerio <Required />
                      </Label><br/>
                      <UploaderMultiple
                        name="resolucion_ministerio"
                        required
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <div className="text-center">
                    <Button
                      type="button"
                      className="btn-round"
                      onClick={handleSendForm}
                      disabled={isLoading || disableSend}
                    >
                      Solicitar
                    </Button>
                  </div>
                </FormGroup>
              </Form>
              <AlertModal
                isOpen={showAlert}
                message={message}
                onClose={alertCallback}
              />
              <ConfirmModal
                isOpen={confirm}
                title={confirmTitle}
                action={confirmAction}
                onClose={() => setConfirm(false)}
                onConfirm={handleConfirmation}
              />
              {TotpModal}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
